/**
 * <load-more-products> custom element
 * Wires up the "VIS FLERE PRODUKTER" button on collection / search pages.
 *
 * Why a custom element instead of an inline <script type="module">?
 * - Inline scripts only execute on initial HTML parse. When Shopify's
 *   facet-link re-renders the section via AJAX (filter, sort, clear), the
 *   <script> tag is re-inserted but does NOT re-execute, so the new button
 *   ends up with no click handler.
 * - Custom elements get a connectedCallback EVERY time they're added to the
 *   DOM, including AJAX-injected HTML and theme-editor reloads. That's what
 *   makes the load-more button work 100% of the time.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    // Guard against duplicate wiring on the same instance (defensive — should
    // not happen because the disconnected old instance is GC'd, but cheap).
    if (this._wired) return;

    const root =
      this.closest('.collection') ||
      this.closest('.search-page') ||
      this.closest('.shopify-section') ||
      document;

    const list = root.querySelector('product-list.product-list');
    const btn = this.querySelector('.load-more-btn');
    let next = root.querySelector('a[rel="next"]');

    // No products list or no button — nothing to do. Don't mark _wired so a
    // future re-connect can retry if DOM changes.
    if (!list || !btn) return;

    // Already on the last page — hide ourselves via .is-done and stop.
    if (!next) {
      this.classList.add('is-done');
      return;
    }

    this._wired = true;
    let loading = false;

    const load = async () => {
      if (loading || !next) return;
      loading = true;
      btn.disabled = true;
      btn.classList.add('is-loading');
      try {
        const res = await fetch(next.href, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newList = doc.querySelector('product-list.product-list');
        const newNext = doc.querySelector('a[rel="next"]');
        if (newList) {
          // Move (not clone) children to preserve attached behaviour
          while (newList.firstElementChild) {
            list.appendChild(newList.firstElementChild);
          }
        }
        next = newNext || null;
        if (!next) this.classList.add('is-done');
      } catch (e) {
        console.warn('[load-more] fetch failed:', e);
      } finally {
        loading = false;
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }
    };

    btn.addEventListener('click', load);
  }
}

if (!window.customElements.get('load-more-products')) {
  window.customElements.define('load-more-products', LoadMoreProducts);
}
